import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Menu, MenuItem, Slider, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useRef, useState } from 'react';
import { useEditor } from '../EditorContext';
import { useAudioWaveform } from './useAudioWaveform';
import WaveformView from './WaveformView';

/* ── Styles ─────────────────────────────────────────────────────── */

const BarWrapper = styled('div')({
    position: 'relative',
    height: 32,
    background: '#0d0d17',
    borderBottom: '1px solid #2a2a3a',
    cursor: 'default',
    overflow: 'hidden',
    userSelect: 'none',
});

const MusicBlock = styled('div')({
    position: 'absolute',
    top: 4,
    height: 24,
    background: 'rgba(100, 181, 246, 0.25)',
    border: '1px solid rgba(100, 181, 246, 0.6)',
    borderRadius: 3,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 6,
    gap: 4,
    overflow: 'hidden',
    minWidth: 16,
    cursor: 'ew-resize',
    boxSizing: 'border-box',
});

const BlockLabel = styled('span')({
    fontSize: 12,
    color: '#90caf9',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    userSelect: 'none',
    pointerEvents: 'none',
    flex: 1,
});

const VolumeChip = styled('span')({
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    fontSize: 11,
    color: '#64b5f6',
    paddingRight: 6,
    flexShrink: 0,
    pointerEvents: 'none',
});

/**
 * MusicTrackBar - renders a single colored block for an audio track (BGM, dub, etc.).
 * The block starts at `track.offsetSeconds` and spans `track._audioDuration` or to end.
 * Drag-to-reposition is supported.
 *
 * Props:
 *   duration  - original video duration (seconds)
 *   track     - { id, name, url, volume, offsetSeconds, _audioDuration? }
 *   zoomLevel - px per second
 *   scrollLeft - current horizontal scroll offset
 */
export default function MusicTrackBar({ duration, track, zoomLevel, scrollLeft, leftOffset = 0 }) {
    const { updateAudioTrack } = useEditor();
    const dragRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const waveformSamples = useAudioWaveform(track?.url);
    // Context menu state for right-click actions (volume, remove, etc.)
    const [menuPos, setMenuPos] = useState(null);

    if (!track) return <BarWrapper />;

    const blockLeft = track.offsetSeconds * zoomLevel + leftOffset;
    const blockWidth = track._audioDuration
        ? track._audioDuration * zoomLevel
        : Math.max((duration - track.offsetSeconds) * zoomLevel, 20);

    /* ── Drag to reposition offset ───────────────────────────── */
    const handleMouseDown = (e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startOffset = track.offsetSeconds;

        const onMove = (me) => {
            const dx = me.clientX - startX;
            const newOffset = Math.max(0, Math.min(startOffset + dx / zoomLevel, duration - 0.5));
            updateAudioTrack(track.id, { offsetSeconds: parseFloat(newOffset.toFixed(2)) });
        };
        const onUp = () => {
            setDragging(false);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        setDragging(true);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const onContextMenu = (e) => {
        e.preventDefault();
        setMenuPos({ mouseX: e.clientX - 2, mouseY: e.clientY - 4 });
    };

    const closeMenu = () => setMenuPos(null);

    const handleVolumeChange = (_, v) => {
        updateAudioTrack(track.id, { volume: v });
    };

    return (
        <BarWrapper style={{ cursor: dragging ? 'ew-resize' : 'default' }}>
            <MusicBlock
                ref={dragRef}
                style={{ left: blockLeft, width: blockWidth, position: 'absolute' }}
                onMouseDown={handleMouseDown}
                onContextMenu={onContextMenu}
                title={`${track.name}  •  vol: ${Math.round(track.volume * 100)}%  •  offset: ${track.offsetSeconds.toFixed(1)}s\nDrag to reposition`}
            >
                {/* Waveform background */}
                {blockWidth > 8 && (
                    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', borderRadius: 3 }}>
                        <WaveformView
                            seed={track.url || track.name || ''}
                            samples={waveformSamples}
                            width={Math.max(4, blockWidth)}
                            height={24}
                            color={waveformSamples ? 'rgba(100,181,246,0.7)' : 'rgba(100,181,246,0.3)'}
                        />
                    </div>
                )}
                <AudiotrackIcon
                    style={{ fontSize: 12, color: '#64b5f6', flexShrink: 0, pointerEvents: 'none', position: 'relative', zIndex: 1 }}
                />
                <BlockLabel style={{ position: 'relative', zIndex: 1 }}>{track.name}</BlockLabel>
                {blockWidth > 70 && (
                    <VolumeChip style={{ position: 'relative', zIndex: 1 }}>
                        <VolumeUpIcon style={{ fontSize: 9 }} />
                        {Math.round(track.volume * 100)}%
                    </VolumeChip>
                )}
            </MusicBlock>

            <Menu
                open={!!menuPos}
                onClose={closeMenu}
                anchorReference="anchorPosition"
                anchorPosition={menuPos ? { top: menuPos.mouseY, left: menuPos.mouseX } : undefined}
            >
                <MenuItem>
                    <div style={{ width: 200, padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Typography variant="caption" sx={{ minWidth: 44 }}>Volume</Typography>
                        <Slider
                            size="small"
                            min={0}
                            max={1}
                            step={0.01}
                            value={track.volume}
                            onChange={handleVolumeChange}
                            sx={{ flex: 1 }}
                        />
                        <Typography variant="caption">{Math.round(track.volume * 100)}%</Typography>
                    </div>
                </MenuItem>
            </Menu>
        </BarWrapper>
    );
}
